import { FormControl, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import { BlockAccessPointDto, BlockSide } from '@yard/app/api'
import { BlockAxisRangeField } from '@yard/components/YardPositionSelector/BlockAxisRangeField'
import { FC, useEffect, useState } from 'react'

export interface BlockAccessPointFormValues {
  side: BlockSide
  minBay: number | null
  maxBay: number | null
  minRow: number | null
  maxRow: number | null
  createPerItem: boolean
}

interface Props {
  id: string
  initialValues?: BlockAccessPointDto
  apiError?: string | null
  bayNames: string[]
  rowNames: string[]
  showCreatePerItem?: boolean
  onSubmit: (values: BlockAccessPointFormValues) => void
}

const SIDES: { value: BlockSide; label: string }[] = [
  { value: 'North', label: 'North' },
  { value: 'South', label: 'South' },
  { value: 'East', label: 'East' },
  { value: 'West', label: 'West' },
]

export const BlockAccessPointForm: FC<Props> = ({
  id,
  initialValues,
  apiError,
  bayNames,
  rowNames,
  onSubmit,
}) => {
  const { t } = useTranslate()
  const theme = useMinimalsTheme()

  const [side, setSide] = useState<BlockSide>(initialValues?.side ?? 'North')
  const [minBay, setMinBay] = useState<number | null>(initialValues?.minBay ?? null)
  const [maxBay, setMaxBay] = useState<number | null>(initialValues?.maxBay ?? null)
  const [minRow, setMinRow] = useState<number | null>(initialValues?.minRow ?? null)
  const [maxRow, setMaxRow] = useState<number | null>(initialValues?.maxRow ?? null)
  const [createPerItem, setCreatePerItem] = useState<boolean>(false)

  const isHorizontal = side === 'North' || side === 'South'

  const rangeMin = isHorizontal ? minBay : minRow
  const rangeMax = isHorizontal ? maxBay : maxRow
  const bothNull = rangeMin === null && rangeMax === null
  const validRange = rangeMin !== null && rangeMax !== null && rangeMax > rangeMin
  const canSplit = bothNull || validRange

  // Clear range fields when side axis changes
  useEffect(() => {
    if (isHorizontal) {
      setMinRow(null)
      setMaxRow(null)
    } else {
      setMinBay(null)
      setMaxBay(null)
    }
  }, [isHorizontal])

  useEffect(() => {
    if (!canSplit && createPerItem) setCreatePerItem(false)
  }, [canSplit, createPerItem])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit({
      side,
      minBay: isHorizontal ? minBay : null,
      maxBay: isHorizontal ? maxBay : null,
      minRow: !isHorizontal ? minRow : null,
      maxRow: !isHorizontal ? maxRow : null,
      createPerItem: canSplit && createPerItem,
    })
  }

  return (
    <Stack
      id={id}
      component='form'
      autoComplete='off'
      noValidate
      onSubmit={handleSubmit}
      spacing={theme.customSpacing.m}
      mt={1}
    >
      <FormControl fullWidth required>
        <InputLabel>{t('side', 'Side')}</InputLabel>
        <Select
          value={side}
          label={t('side', 'Side')}
          onChange={e => setSide(e.target.value as BlockSide)}
        >
          {SIDES.map(s => (
            <MenuItem key={s.value} value={s.value}>
              {s.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {isHorizontal && (
        <BlockAxisRangeField
          label={t('bay', 'Bay')}
          axisNames={bayNames}
          minValue={minBay}
          maxValue={maxBay}
          onChange={(min, max) => {
            setMinBay(min)
            setMaxBay(max)
          }}
        />
      )}

      {!isHorizontal && (
        <BlockAxisRangeField
          label={t('row', 'Row')}
          axisNames={rowNames}
          minValue={minRow}
          maxValue={maxRow}
          onChange={(min, max) => {
            setMinRow(min)
            setMaxRow(max)
          }}
        />
      )}

      {apiError && (
        <Typography variant='caption' color='error'>
          {apiError}
        </Typography>
      )}
    </Stack>
  )
}
